import { credentials } from './gmb_api';
import { Fragment }    from 'react';
import { useEffect }   from 'react';
import { useRef }      from 'react';
import { useState }    from 'react';
import Alert           from '../../components/alert';
import connectProgress from './connect_progress';
import NotFoundPage    from '../../components/not_found_page';
import React           from 'react';
import Rollbar         from '../../rollbar';
import Swal            from 'sweetalert2';
import useScript       from './use_script';


const Loading = connectProgress(LoadingView);

export default function PlacesAPISearch({ business, saveGooglePlace }) {
  const scriptURL             = `https://maps.googleapis.com/maps/api/js?key=${credentials.apiKey}&libraries=places`;
  const [ completed, failed ] = useScript(scriptURL);

  const isLoading = !completed;
  const isLoaded  = completed && !failed;
  const isFailed  = completed && failed;

  return (
    <Fragment>
      {isLoading && <Loading/>}
      {isFailed && <Error/>}
      {isLoaded && <AutocompleteView {...{ business, saveGooglePlace }}/>}
    </Fragment>
  );
}


function AutocompleteView({ saveGooglePlace }) {
  const [ place, setPlace ] = useState();
  const [ error, setError ] = useState();

  return (
    <div className="list block inset places-autocomplete">
      <h4>Connect your Google Location</h4>
      <p>Enter your location name below and choose a location from the list of suggestions.</p>
      {error && <PlaceError {...{ error }}/>}
      <SearchInput {...{ setPlace, setError }}/>
      {place && <PlaceInfo {...{ place, saveGooglePlace }}/>}
    </div>
  );
}

const autocompleteOptions = {
  fields: [
    'address_components',
    'formatted_address',
    'formatted_phone_number',
    'geometry',
    'name',
    'place_id',
    'types',
    'url',
    'website'
  ],
  types: [
    'establishment'
  ]
};

function SearchInput({ setPlace, setError }) {

  const ref = useRef();

  useEffect(function setupAutocomplete() {
    const input            = ref.current;
    const { Autocomplete } = window.google.maps.places;
    const autocomplete     = new Autocomplete(input, autocompleteOptions);

    function clearError() {
      setError(null);
    }

    input.addEventListener('input', clearError);
    input.addEventListener('focus', clearError);

    autocomplete.addListener('place_changed', function() {
      const place      = autocomplete.getPlace();
      const isNameOnly = isNameOnlyPlace(place);
      const isValid    = isValidPlace(place);

      // When the user types and presses return without choosing a suggestion,
      // we get a place like `{ name: 'asdf' }`. We don't want to show an error
      // in that case. Just ignore it until they choose a suggestion.
      if (isNameOnly)
        setPlace(null);

      // A valid place has a place_id and a cid (derived from place.url).
      else if (isValid)
        setPlace(place);

      else {
        setPlace(null);
        setError('Sorry, that location cannot be connected. Please select a different location.');
        Rollbar.error('Google Places Autocomplete: an invalid place was chosen', place);
      }
    });

    return () => {
      window.google.maps.event.clearInstanceListeners(autocomplete);
      input.removeEventListener('input', clearError);
      input.removeEventListener('focus', clearError);
    };
  }, [ setError, setPlace ]);

  useEffect(function preventTouchEndPropagationBug() {
    // prevent touchend propagation on iOS, to allow the place_changed event to be triggered
    // cf. https://gist.github.com/schoenobates/ef578a02ac8ab6726487

    let timer;
    let nodes      = [];
    const platform = window.device?.platform;
    const isiOS    = platform === 'iOS';

    function stopPropagation(e) {
      e.stopImmediatePropagation();
    }
    function preventTouchEndPropagation() {
      nodes = Array.from(document.querySelectorAll('.pac-container'));
      nodes.forEach(node => node.addEventListener('touchend', stopPropagation));
    }

    if (isiOS)
      timer = setTimeout(preventTouchEndPropagation, 500);

    return () => {
      clearTimeout(timer);
      nodes.forEach(node => node.removeEventListener('touchend', stopPropagation));
    };
  });

  useEffect(function() {
    // fastclick interferes with places autocomplete
    // https://stackoverflow.com/questions/9972080/cant-tap-on-item-in-google-autocomplete-list-on-mobile
    function disableFastClick() {
      const nodes = Array.from(document.querySelectorAll('.pac-item, .pac-item span'));
      nodes.forEach(node => node.classList.add('no-fastclick'));
    }
    document.addEventListener('DOMNodeInserted', disableFastClick);
    return () => {
      document.removeEventListener('DOMNodeInserted', disableFastClick);
    };
  });

  return (
    <input {...{
      ref,
      type:           'text',
      placeholder:    'Search for your location',
      autoComplete:   'off',
      autoCorrect:    'off',
      autoCapitalize: 'off',
      spellCheck:     'false'
    }}/>
  );
}


function PlaceError({ error }) {
  return (
    <Alert type="error">{error}</Alert>
  );
}


function PlaceInfo({ place, saveGooglePlace }) {

  async function onClick() {
    const cid = getPlaceCID(place);
    await saveGooglePlace({ cid, place });
    Swal.fire({
      text: 'Your location has been connected!',
      icon: 'success'
    });
  }

  return (
    <Fragment>
      <div className="place-info">
        <div className="place-info--text">
          <div className="place-info--name">{place.name}</div>
          <div className="place-info--address">{place.formatted_address}</div>
        </div>
      </div>
      <button className="button button-fill" onClick={onClick}>Connect this location</button>
    </Fragment>
  );
}


function Error() {
  Rollbar.error('Failed to load Google Maps API.');
  return (<NotFoundPage title="Oops! We’re having trouble connecting to Google."/>);
}


function LoadingView({ progressStart, progressEnd }) {
  useEffect(() => {
    progressStart();
    return progressEnd;
  }, [ progressStart, progressEnd ]);
  return null;
}


function isValidPlace(place) {
  const cid = place && getPlaceCID(place);
  return !!(cid && place.place_id);
}

function isNameOnlyPlace(place) {
  try {
    const keys = Object.keys(place);
    return (keys.length === 1 && keys[0] === 'name');
  } catch (error) {
    return false;
  }
}

function getPlaceCID(place) {
  try {
    const url = new URL(place.url);
    const cid = url.searchParams.get('cid');
    return cid;
  } catch (error) {
    return undefined;
  }
}
